import React from 'react';
import DirectNavigationModel from '../DirectNavigationModel';
import {PropTypes} from 'prop-types';
import StructuresView from '../StructuresView/StructuresView.react';
import GroupsView from '../GroupsView/GroupsView.react';
import SpacesView from '../SpacesView/SpacesView.react';
import {TrapFocus} from '@homeaway/a11y-tools';

import './NavigationView.less';

class NavigationView extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {};
        this.trapFocus = new TrapFocus();
        this.rootRef = React.createRef();
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        // We should configure an "original" navigation state here based on props.
        // Navigation view then composes the views for structures, floors, and spaces
        // managing the children for each.
        let propsNavigationString = JSON.stringify(nextProps.navigation);
        if (
            propsNavigationString !== prevState.propsNavigationString ||
            nextProps.sphereId !== prevState.sphereId
        ) {
            let navigation = JSON.parse(
                propsNavigationString !== prevState.propsNavigationString
                    ? propsNavigationString
                    : JSON.stringify(prevState.navigation)
            );
            NavigationView._markActiveLocations(navigation, nextProps.sphereId);
            return {
                propsNavigationString,
                navigation,
                sphereId: nextProps.sphereId,
                structuresIndex: navigation.structures
                    ? navigation.structures.findIndex((structure) => structure.active)
                    : -1,
            };
        }
        return prevState;
    }

    componentDidMount(): void {
        const node = this.rootRef.current;
        this.trapFocus.determineFocusable(node);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const node = this.rootRef.current;
        this.trapFocus.determineFocusable(node);
    }

    static _markActiveLocations(navigation, sphereId) {
        let {structure, group, space} = DirectNavigationModel.getActiveElements(
            navigation,
            sphereId
        );

        [structure, group, space].forEach((element) => {
            if (element) {
                element.active = true;
            }
        });
    }

    _onSpaceSelected = (sphereId) => {
        this.props.onSelectNavSpace(sphereId);
    };

    _onStructureClick = (structure, index) => {
        this.setState({
            ...this.state,
            structuresIndex: index,
        });
    };

    _onGroupClick = (group, structure) => {
        let newNavigation = JSON.parse(JSON.stringify(this.state.navigation));
        let newGroups;
        if (structure) {
            let newStructure = newNavigation.structures.find(
                (element) => element.id === structure.id
            );
            newGroups = newStructure.groups;
        } else {
            newGroups = newNavigation.groups;
        }
        newGroups.forEach((newGroup) => {
            newGroup.active = newGroup.id === group.id && !group.active;
        });
        if (group.landingSphere) {
            this._onSpaceSelected(group.landingSphere.id);
        }
        this.setState({
            ...this.state,
            navigation: newNavigation,
        });
    };

    _onSpaceClick = (space, group, structure) => {
        this._onSpaceSelected(space.landingSphere.id);
    };

    _handleKeyDown = (e) => {
        if (e.keyCode === 9 && this.props.shouldTrapFocus) {
            // tab key
            this.trapFocus.handleTabbing(e);
        }
    };

    _getStructuresView() {
        return (
            <StructuresView
                structures={this.state.navigation.structures}
                activeIndex={this.state.structuresIndex}
                onStructureClick={this._onStructureClick}
                onGroupClick={this._onGroupClick}
                onSpaceClick={this._onSpaceClick}
            />
        );
    }

    _getGroupsView() {
        return (
            <GroupsView
                groups={this.state.navigation.groups}
                onGroupClick={this._onGroupClick}
                onSpaceClick={this._onSpaceClick}
                useMediaGateway={this.props.useMediaGateway}
            />
        );
    }

    _getSpacesView() {
        return (
            <SpacesView
                spaces={this.state.navigation.spaces}
                onSpaceClick={this._onSpaceClick}
                useMediaGateway={this.props.useMediaGateway}
            />
        );
    }

    _getRootView() {
        let navigation = this.state.navigation;
        let root = null;
        if (navigation.structures) {
            root = this._getStructuresView();
        } else if (navigation.groups) {
            root = this._getGroupsView();
        } else if (navigation.spaces) {
            root = this._getSpacesView();
        }
        return root;
    }

    render() {
        return (
            <form className={'NavigationView'} onKeyDown={this._handleKeyDown}>
                <fieldset disabled={this.props.disabled} ref={this.rootRef}>
                    {this._getRootView()}
                </fieldset>
            </form>
        );
    }
}

NavigationView.propTypes = {
    navigation: DirectNavigationModel.navigationType.isRequired,
    sphereId: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onSelectNavSpace: PropTypes.func,
    shouldTrapFocus: PropTypes.bool,
    useMediaGateway: PropTypes.bool,
};

export default NavigationView;
